"use client";

import { ArrowRight, LayoutGrid } from "lucide-react";
import Link from "next/link";

import { featureData } from "@/app/features/[slug]/_data/features";

import type { FeatureInfo } from "@/app/features/[slug]/_data/features";

/** Landing grid order: deploy first, then runtime, then everything attached to it. */
const landingSlugs = [
	"auto-deploys",
	"workers",
	"database",
	"queues",
	"workflows",
	"cron",
	"ai",
	"auth",
	"sandboxes",
	"storage",
	"file-storage",
	"static-sites",
	"ploy-git",
	"self-hosting",
];

export const features: FeatureInfo[] = landingSlugs.flatMap((slug) => {
	const feature = featureData[slug];
	return feature ? [feature] : [];
});

export default function Features() {
	return (
		<section
			id="features"
			className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8"
		>
			<div className="mb-16 max-w-3xl">
				<p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
					Platform
				</p>
				<h2 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
					Everything an app or agent needs to run in production
				</h2>
				<p className="text-lg leading-relaxed text-muted-foreground">
					Push to deploy, and attach databases, queues, workflows, schedules,
					storage, sandboxes, model access, and auth with a line of ploy.yaml.
					No glue infrastructure to assemble yourself, and the whole platform is
					open source.
				</p>
			</div>

			<div className="grid grid-cols-1 border-l border-t border-border md:grid-cols-2 lg:grid-cols-3">
				{features.map((feature) => (
					<FeatureCard key={feature.slug} {...feature} />
				))}
				<Link
					prefetch={true}
					href="/features"
					className="group flex h-full flex-col justify-between border-b border-r border-border bg-muted/30 p-8 transition-colors hover:bg-muted/60"
				>
					<LayoutGrid className="mb-6 size-5 text-muted-foreground" />
					<div>
						<div className="mb-3 flex items-start justify-between gap-2">
							<h3 className="text-xs font-semibold uppercase tracking-widest">
								All features
							</h3>
							<ArrowRight className="mt-0.5 size-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
						</div>
						<p className="text-sm leading-relaxed text-muted-foreground">
							Browse every product with code examples and links to the docs.
						</p>
					</div>
				</Link>
			</div>
		</section>
	);
}

const FeatureCard = ({
	slug,
	icon: Icon,
	color,
	title,
	summary,
}: FeatureInfo) => {
	return (
		<Link
			prefetch={true}
			href={`/features/${slug}`}
			className="group flex h-full flex-col border-b border-r border-border p-8 transition-colors hover:bg-muted/50"
		>
			<Icon className={`mb-6 size-5 ${color}`} />

			<div className="mb-3 flex items-start justify-between gap-2">
				<h3 className="text-xs font-semibold uppercase tracking-widest">
					{title}
				</h3>
				<ArrowRight className="mt-0.5 size-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
			</div>

			<p className="text-sm leading-relaxed text-muted-foreground">{summary}</p>
		</Link>
	);
};
